// Canonical route paths — mirrors src/lib/navigation/routePaths.ts in the root
// Lovable app (single source of truth for the Beta G2 surfaces). Kept in sync
// by hand because this app is standalone (no npm workspace import).
//
// Naming contract (David 2026-06-09): the education surface is "Recursos" at
// `/recursos`, NOT `/academia`. The hero/title MAY say "Academia Umbral", but
// the URL and the nav say Recursos.
export const ROUTES = {
  home: "/",
  chat: "/chat",
  noticias: "/noticias",
  noticiaDetalle: "/noticias/:slug",
  recursos: "/recursos",
  // Static resource detail pages (P1): minimal ports of the handoff LLM course
  // and Cowork guide. No backend — static, readable content only.
  recursoLlm: "/recursos/llm",
  recursoMcp: "/recursos/mcp",
  recursoCowork: "/recursos/cowork",
  // Fase B3 (sub-PR 1/3) — interactive single-screen resources ported from
  // the v8 handoff's resource-kit.jsx mini-apps.
  recursoIfc: "/recursos/ifc-openbim",
  recursoCde: "/recursos/cde-iso19650",
  recursoProgramacionVisual: "/recursos/programacion-visual",
  // PKG-RECURSOS-V12 — Skills: procedimientos reutilizables para agentes
  // (6-module course, same CourseShell as LLM/MCP). See RecursoSkillsPage +
  // content/skillLessons.
  recursoSkills: "/recursos/skills",
  // UX-06 — public read-only view of a shared conversation (no BetaGate).
  sharePublic: "/share/:slug",
  // UX-09 — public static pricing (bags only; subs = próximamente).
  precios: "/precios",
  // UX-16 — marketing/campaign surfaces (Wave 2).
  academia: "/academia",
  guia: "/guia",
  recursoTokenflow: "/recursos/tokenflow",
  campana: "/campana/:slug?",
  // UX-14 — diagnóstico BIM MVP (client-side deliverables).
  diagnostico: "/diagnostico",
  // UX-17 — service quote request.
  servicios: "/servicios",
  // PKG-MKT-LEGAL-ROUTES — standalone, crawlable legal pages (Marketplace
  // listing requires public Privacy + Support URLs, not modal-only content).
  legalPrivacidad: "/legal/privacidad",
  legalSoporte: "/legal/soporte",
  devIcons: "/dev/icons",
  devChatFixture: "/dev/chat-fixture",
  devIconUploadFixture: "/dev/icon-upload-fixture",
  adminIcons: "/admin/icons",
  // Mega-ola 1 (ADM-3) — founder admin dashboard. Access decided server-side
  // (GET /admin/overview: 200 founder · 404 anyone else → redirect /chat).
  admin: "/admin",
  // Mega-ola 2 (TEAM-3) — team portal + invite acceptance. Both mount only
  // when VITE_TEAM_PORTAL_ENABLED === "true" (build-time flag).
  teamSettings: "/settings/team",
  teamJoin: "/team/join",
  // Mega-ola 3 prep — IFC viewer page shell (/modelo/:id). Shell only:
  // placeholder viewport, no ThatOpen bundle (VITE_IFC_VIEWER_ENABLED=false).
  modelo: "/modelo/:id",
  // Legacy/short alias kept working — redirects to recursoCowork (P2 gap).
  coworkLegacy: "/cowork",
  // PKG-WS-WORKSHOP-FE-20260728 — PERMANENT landing for the printed QR on the
  // 80×200 roll-up (UTFSM). The banner cannot be reprinted, so this path is
  // frozen forever and must NEVER 404: see docs/operations/workshop-ws-route-2026-07-28.md.
  ws: "/ws",
  // PKG-MKT-TRANSACT-LANDING — where Microsoft Marketplace drops a buyer after
  // they pay (`?token=<purchase token>`). Reachable 24/7 and never gated: see
  // docs/microsoft-marketplace/transact-api-runbook-2026-08.md. Deliberately
  // absent from PRIMARY_NAV — nobody navigates here, they arrive from Azure.
  marketplaceActivate: "/marketplace/activate",
} as const;

export type RouteKey = keyof typeof ROUTES;

// Primary header nav (order matters): Chat · Noticias · Recursos.
export const PRIMARY_NAV: ReadonlyArray<{ label: string; path: string }> = [
  { label: "Chat", path: ROUTES.chat },
  { label: "Noticias", path: ROUTES.noticias },
  { label: "Recursos", path: ROUTES.recursos },
];
